import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import * as z from "zod";
import type { FormSubmitEvent } from "#ui/types";

import { useAuthStore } from "~/stores/auth";

type Schema = z.output<typeof schema>;


export default /*@__PURE__*/_defineComponent({
  __name: 'LoginForm',
  emits: ["forgot-password", "error"],
  setup(__props, { emit: __emit }) {

const config = useRuntimeConfig();
const { $api } = useNuxtApp();
const auth = useAuthStore();

const emit = __emit;

const errorMessage = ref<string>();
const loading = ref(false);
// Watch for changes on errorMessage
watch(errorMessage, (newValue) => {
  if (newValue) {
    setTimeout(() => {
      errorMessage.value = undefined;
    }, 5000);
  }
});

const fields = [
  {
    name: "email",
    type: "text" as const,
    label: "Email",
    placeholder: "Enter your email",
    required: true,
  },
  {
    name: "password",
    label: "Password",
    type: "password" as const,
    placeholder: "Enter your password",
    required: true,
  },
  {
    name: "remember",
    label: "Remember me",
    type: "checkbox" as const,
  },
];

const providers = !config.public.prelaunch
  ? [
      {
        label: "Continue with Google",
        icon: "i-mdi:google",
        onClick: () => {
          window.location.href = $api("/auth/google/redirect");
        },
      },
    ]
  : [];

const schema = z.object({
  email: z.string().email("Invalid email"),
  password: z.string().min(8, "Must be at least 8 characters"),
  remember: z.boolean().optional(),
});

function redirect() {
  navigateTo("/auth/redirect", {
    external: true,
  });
}

async function onSubmit(payload: FormSubmitEvent<Schema>) {
  try {
    loading.value = true;
    const rememberedLogin = payload.data.remember || false;
    await auth.login(payload.data, rememberedLogin);
    loading.value = false;
    redirect();
  } catch (error) {
    errorMessage.value =
      "The email address or password you entered is incorrect. Please try again.";
    if (error.response?._data?.errors?.E_TOO_MANY_REQUESTS) {
      errorMessage.value = error.response._data.errors.E_TOO_MANY_REQUESTS;
    }
    if (error.response?.data?.errors?.message) {
      errorMessage.value = error.response.data.errors.message;
    }
    emit("error", errorMessage.value);
  } finally {
    loading.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_NuxtLink = _resolveComponent("NuxtLink")!
  const _component_UButton = _resolveComponent("UButton")!
  const _component_UAlert = _resolveComponent("UAlert")!
  const _component_UAuthForm = _resolveComponent("UAuthForm")!

  return (_openBlock(), _createBlock(_component_UAuthForm, {
    loading: _unref(loading),
    fields: fields,
    providers: _unref(providers),
    schema: _unref(schema),
    title: "Sign in to your account",
    align: "top",
    icon: "i-lucide:log-in",
    class: "bg-transparent",
    ui: { root: 'space-y-6', footer: 'text-sm text-gray-500 dark:text-gray-400' },
    onSubmit: onSubmit
  }, {
    description: _withCtx(() => [
      _cache[2] || (_cache[2] = _createTextVNode(" Or ", -1)),
      _createVNode(_component_NuxtLink, {
        to: "/signup",
        class: "font-medium text-purple-600 hover:text-purple-500 dark:text-purple-400"
      }, {
        default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode(" Create Account ", -1)
        ]))]),
        _: 1
      })
    ]),
    "password-hint": _withCtx(() => [
      _createVNode(_component_UButton, {
        variant: "link",
        color: "purple",
        class: "text-sm font-medium text-purple-600 hover:text-purple-500 dark:text-purple-400",
        label: "Forgot password?",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('forgot-password')))
      })
    ]),
    validation: _withCtx(() => [
      (_unref(errorMessage))
        ? (_openBlock(), _createBlock(_component_UAlert, {
            key: 0,
            color: "red",
            variant: "soft",
            icon: "i-heroicons-exclamation-triangle-20-solid",
            title: "Authentication Error",
            description: _unref(errorMessage),
            class: "mb-4"
          }, null, 8, ["description"]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _cache[5] || (_cache[5] = _createTextVNode(" By signing in, you agree to our ", -1)),
      _createVNode(_component_NuxtLink, {
        to: "/terms",
        class: "font-medium text-gray-900 dark:text-white underline"
      }, {
        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
          _createTextVNode("Terms of Service", -1)
        ]))]),
        _: 1
      }),
      _cache[6] || (_cache[6] = _createTextVNode(" and ", -1)),
      _createVNode(_component_NuxtLink, {
        to: "/privacy-policy",
        class: "font-medium text-gray-900 dark:text-white underline"
      }, {
        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
          _createTextVNode("Privacy Policy", -1)
        ]))]),
        _: 1
      }),
      _cache[7] || (_cache[7] = _createTextVNode(". ", -1))
    ]),
    _: 1
  }, 8, ["loading", "providers", "schema"]))
}
}

})